import { Box, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import LiveStatusBadge from './LiveStatusBadge'
import { liveDashboardUrl, useLiveEmbed } from './useLiveEmbed'

// Live dashboards are laid out for a 1920×1080 screen; the preview shows the whole page scaled down
// to the card's width, like the screenshots (width: 100%).
const PAGE_WIDTH = 1920
const PAGE_HEIGHT = 1080

interface LiveDashboardPreviewProps {
  siteId: string
  dashboardId: string
  title: string
  onOpen: () => void
}

/**
 * Live thumbnail of a cloud dashboard: the live app in a scaled-down iframe with its status badge.
 * The iframe (a full app and a hub connection) is only mounted while the card is visible in the row.
 */
const LiveDashboardPreview = ({ siteId, dashboardId, title, onOpen }: LiveDashboardPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [visible, setVisible] = useState(false)
  const [scale, setScale] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const visibility = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting))
    const size = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / PAGE_WIDTH))
    visibility.observe(container)
    size.observe(container)
    return () => {
      visibility.disconnect()
      size.disconnect()
    }
  }, [])

  const src = visible ? liveDashboardUrl(siteId, dashboardId, { embed: true }) : undefined
  const state = useLiveEmbed(iframeRef, src)

  // Same layout as a screenshot card (preview, then title), with the status badge under the title.
  return (
    <>
      <Box
        ref={containerRef}
        onClick={onOpen}
        sx={{
          width: '100%',
          aspectRatio: `${PAGE_WIDTH} / ${PAGE_HEIGHT}`,
          overflow: 'hidden',
          borderRadius: '6px',
          backgroundColor: '#1e1e1e',
          cursor: 'zoom-in',
          opacity: state.status === 'unavailable' ? 0.5 : 1,
        }}
      >
        {src ? (
          <iframe
            ref={iframeRef}
            src={src}
            title={title}
            tabIndex={-1}
            style={{
              width: PAGE_WIDTH,
              height: PAGE_HEIGHT,
              border: 0,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </Box>
      <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <LiveStatusBadge state={state} variant="tinted" />
      </Box>
    </>
  )
}

export default LiveDashboardPreview
